import React, { createContext, useContext, useLayoutEffect } from 'react';
import { setGlobalReadOnly } from '../lib/api-client';
import { EyeOff } from 'lucide-react';

interface ReadOnlyContextValue {
  /** true when the current route is in read-only mode for this user */
  isReadOnly: boolean;
}

const ReadOnlyContext = createContext<ReadOnlyContextValue>({ isReadOnly: false });

/**
 * Provider that wraps route content to signal read-only mode.
 * - Syncs the global read-only flag in api-client.ts so all write API calls are blocked
 * - Shows a subtle "Read Only" banner when active
 */
export const ReadOnlyProvider: React.FC<{
  isReadOnly: boolean;
  hideBanner?: boolean;
  children: React.ReactNode;
}> = ({ isReadOnly, hideBanner = false, children }) => {
  // Sync the global axios interceptor flag whenever isReadOnly changes.
  // useLayoutEffect ensures the flag is set synchronously before the browser
  // paints, preventing a timing gap during route transitions where the flag
  // could briefly be false between unmount cleanup and new mount.
  useLayoutEffect(() => {
    setGlobalReadOnly(isReadOnly);
    // Clean up on unmount — reset to non-read-only
    return () => setGlobalReadOnly(false);
  }, [isReadOnly]);

  return (
    <ReadOnlyContext.Provider value={{ isReadOnly }}>
      {isReadOnly && !hideBanner && (
        <div className="bg-amber-50 border border-amber-200  px-4 py-4 mb-4 items-center gap-2 justify-center inline-flex">
          <EyeOff className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-800">
            Read Only Mode
          </span>
        </div>
      )}
      {children}
    </ReadOnlyContext.Provider>
  );
};

/**
 * Hook to check if the current route is in read-only mode.
 *
 * Usage in any component:
 *   const { isReadOnly } = useReadOnly();
 *   <button disabled={isReadOnly}>Create</button>
 */
export function useReadOnly(): ReadOnlyContextValue {
  return useContext(ReadOnlyContext);
}
